import { supabase } from "@/integrations/supabase/client";

/**
 * Creates a bill component in Kasflow via the Infradigital API
 */
export const createBillComponent = async (billData: any): Promise<string> => {
  try {
    console.log("🔄 Starting createBillComponent with data:", JSON.stringify(billData, null, 2));
    
    // Call the infradigital Edge Function directly
    const { data: response, error } = await supabase.functions.invoke('infradigital', {
      body: billData
    });

    if (error) {
      console.error('❌ Bill component creation error:', error);
      throw new Error(`Failed to create bill component: ${error.message}`);
    }

    if (!response || !response.data) {
      console.error('❌ No bill_component_id received from Infradigital');
      throw new Error('Failed to create bill component: No bill component ID received');
    }

    console.log('✅ Bill component creation response:', response);
    return response.data;

  } catch (error) {
    console.error('❌ Error in createBillComponent:', error);
    throw error;
  }
};

/**
 * Creates a direct testing endpoint to diagnose Infradigital integration issues
 * Updated to use infradigital function for reliability
 */
export const debugBillComponentCreation = async (billKey: string, amount: number, notes: string): Promise<any> => {
  try {
    console.log("🔄 Testing bill component creation with direct debugging...");
    
    // Create test data for the bill
    const testData = {
      "create_by": "debug@test.com",
      "biller_code": "60000",
      "bill_key": billKey || "12345",
      "account_code": "MANDIRI_IDN",
      "bill_component_name": `DEBUG_TEST_${Date.now()}`,
      "expiry_date": new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      "due_date": new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
      "active_date": new Date().toISOString(),
      "amount": amount || 10.00,
      "amount_currency": "IDR",
      "penalty_amount": 0,
      "batch_id": new Date().toISOString(),
      "notes": notes || "Debug test bill component"
    };
    
    // Use the infradigital function that is working properly
    const { data, error } = await supabase.functions.invoke('infradigital', {
      body: { data: testData }
    });

    if (error) {
      console.error('❌ Debug bill component creation error:', error);
      throw new Error(`Failed to create debug bill component: ${error.message}`);
    }
    
    console.log('✅ Debug bill component creation response:', data);
    return data;
  } catch (error) {
    console.error('❌ Error in debugBillComponentCreation:', error);
    throw error;
  }
};

/**
 * Tests the bill component creation API without actually creating a bill
 * This is useful for debugging configuration issues
 */
export const testBillComponentConfig = async (): Promise<any> => {
  try {
    console.log("🔄 Testing infradigital configuration...");
    const { data, error } = await supabase.functions.invoke('debug-bill-component', {
      body: { testMode: true }
    });

    if (error) {
      console.error('❌ Infradigital config test error:', error);
      throw new Error(`Failed to test infradigital configuration: ${error.message}`);
    }
    
    console.log('✅ Infradigital configuration test successful:', data);
    return data;
  } catch (error) {
    console.error('❌ Error in testBillComponentConfig:', error);
    throw error;
  }
};
